import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, Link, IconButton, Tooltip } from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBackIos'
import { Link as RouterLink, useParams, navigate } from '@reach/router'
import { useDispatch, useSelector } from 'react-redux'

import EditIcon from '_assets/icons/ic-edit.svg'
import Svg, { ICON } from '_components/svg'
import Button from '_components/button'
import useRolePermission from '_hooks/use-role-permission'
import { getBudgetByIdSelector } from '_modules/budget/selectors'
import { SendBudgetByEmailButton, SendBudgetByWhatsAppButton } from '_components/service-order'
import ModalDialog from '_components/modal/modal-dialog'
import { Loader } from '@refera/ui-web'

import ModalConfirmInstallment from '../components/ModalConfirmInstallment'

import useStyles from './styles'
import { prepareBudgetResolution } from '_/modules/budget/actions'
import { useToast } from '_/hooks/use-toast'
import { PERMISSIONS, PERMISSIONS_ACTIONS } from '_/utils/constants/permissions'
import { ROUTES } from '_/utils/constants'
import { getGenericParametersSelector } from '_/modules/service-orders/selectors'
import { getGenericParameters, getServiceOrder } from '_/modules/service-orders/actions'
import { GENERIC_PARAMETERS } from '_/utils/constants/service-order'
import { userSelector } from '_/modules/authentication/selectors'
import useBudgetPdfFileName from '_hooks/use-budget-pdf-file-name'

const Actions = ({
  isCancelled,
  serviceOrderId,
  onToggleEdit,
  isEdit,
  isOriginalBudget,
  onCancelEditClick,
  onBackToServiceOrderClick,
  onSaveClick,
  isSavingLoading,
}) => {
  const styles = useStyles({ isCancelled })
  const dispatch = useDispatch()
  const { budgetId } = useParams()
  const budget = useSelector(getBudgetByIdSelector(budgetId))
  const genericParameters = useSelector(getGenericParametersSelector)
  const pdfFileName = useBudgetPdfFileName({ budgetId })

  const { checkUserPermission, isSAAS, isAdmin, isIntermediary } = useRolePermission()
  const [modalDialog, setModalDialog] = useState({ isOpen: false, subTitle: '' })
  const { showToast } = useToast()

  // TODO: Remove this once BE send this info
  const originalBudgetId = isOriginalBudget ? undefined : budget.budgetParent
  const editableBudgetId = isOriginalBudget ? Number(budgetId) + 1 : undefined
  const [modalOpen, setModalOpen] = useState(false)
  const [isValidatingBudget, setIsValidatingBudget] = useState(false)
  const [files, setFiles] = useState([])
  const isSimplified = budget?.isSimplified

  const user = useSelector(userSelector)
  const { useNewBudget = false } = user?.params || {}
  const isNewBudgetFeatureActive = useMemo(
    () =>
      genericParameters?.find(item => item.name === 'new_budget_news_feature_toggle')?.active &&
      useNewBudget,
    [genericParameters, useNewBudget]
  )

  const isLoading = useMemo(() => isValidatingBudget, [isValidatingBudget])

  const backToServiceOrderButtonProps = useMemo(() => {
    if (isEdit) {
      return {
        onClick: onBackToServiceOrderClick,
      }
    }

    return {
      to: `/chamado/${serviceOrderId}`,
    }
  }, [onBackToServiceOrderClick, isEdit, serviceOrderId])

  useEffect(() => {
    if (isSimplified) {
      const resolutions = budget?.budgetResolution?.toJS() || []
      const resolutionAttachments = resolutions.flatMap(
        resolution => resolution?.problemMedia ?? []
      )
      const budgetPictures = budget?.budgetPictures?.toJS() || []

      setFiles([...resolutionAttachments, ...budgetPictures])
    }
  }, [isSimplified, budget?.budgetResolution, budget?.budgetPictures])

  const isSafeProtocol = rawUrl => {
    try {
      const parsed = new URL(rawUrl, window.location.origin)
      return ['https:', 'http:'].includes(parsed.protocol)
    } catch {
      return false
    }
  }

  function isFilePDF(file) {
    const url = file?.picture
    if (!url) return false

    try {
      return new URL(url).pathname.toLowerCase().endsWith('.pdf')
    } catch {
      return url.toLowerCase().includes('.pdf')
    }
  }

  const handleDownloadButton = useCallback(async () => {
    const file = files?.find(f => !(f instanceof File) && f?.picture && isFilePDF(f))
    const url = file?.picture

    if (!url) {
      showToast({ type: 'error' })
      return
    }

    if (!isSafeProtocol(url)) {
      showToast({ type: 'error' })
      return
    }

    try {
      setIsValidatingBudget(true)

      const response = await fetch(url)

      if (!response.ok) {
        showToast({ type: 'error' })
        return
      }

      const blob = await response.blob()
      const objectUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = objectUrl
      a.download = pdfFileName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(objectUrl)
    } catch (error) {
      console.error('Erro ao baixar PDF do orçamento simplificado:', error)
      showToast({
        type: 'error',
      })
    } finally {
      setIsValidatingBudget(false)
    }
  }, [files, pdfFileName, showToast])

  const handleValidBudget = useCallback(() => {
    if (isSimplified) {
      handleDownloadButton()
    } else {
      setModalOpen(true)
    }
  }, [isSimplified, handleDownloadButton])

  const showSendBudgetByEmailButton = useMemo(
    () => isIntermediary || isAdmin,
    [isEdit, isIntermediary, isAdmin]
  )
  const handleEditClick = useCallback(async () => {
    let url = `${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamentos/${budgetId}`
    try {
      if (isSimplified) {
        url = `${ROUTES.SERVICE_ORDERS}/${serviceOrderId}/orcamento-simplificado/${budgetId}`
      }
      if (isNewBudgetFeatureActive) {
        await dispatch(prepareBudgetResolution(budgetId))
        const params = { edit: true }
        if (isSAAS) {
          const budgetType = budget?.get('proposeOpened') === true ? 'interno' : 'externo'
          params.tipo = budgetType
        }
        await dispatch(getServiceOrder(serviceOrderId))
        if (Object.keys(params)?.length) {
          url += `?${new URLSearchParams(params).toString()}`
        }
        navigate(url)
      } else {
        onToggleEdit()
      }
    } catch (error) {
      console.error('Erro editar orçamento', error)
      showToast({ message: `Erro ao acessar edição de orçamento.`, type: 'error' })
    }
  }, [onToggleEdit, serviceOrderId, budgetId, isNewBudgetFeatureActive, isSimplified, dispatch])

  useEffect(() => {
    dispatch(getGenericParameters({ name: GENERIC_PARAMETERS.NEW_BUDGET_NEWS_FEATURE_TOGGLE }))
  }, [dispatch])

  return (
    <>
      <Loader hasBackdrop open={isLoading} />
      <Grid
        container
        alignItems="center"
        justifyContent="space-between"
        wrap="nowrap"
        className={styles.header}
      >
        {isOriginalBudget ? (
          <Button
            to={`/chamado/${serviceOrderId}/orcamento/${editableBudgetId}`}
            color="secondary"
            startIcon={<ArrowBackIcon className={styles.buttonIcon} />}
          >
            Voltar ao orçamento
          </Button>
        ) : (
          <Button
            color="secondary"
            startIcon={<ArrowBackIcon className={styles.buttonIcon} />}
            {...backToServiceOrderButtonProps}
          >
            Voltar ao pedido
          </Button>
        )}
        <Grid className={styles.headerButtons}>
          {!isOriginalBudget && (
            <Grid className={styles.buttons}>
              {isEdit && (isSAAS || isAdmin) ? (
                <>
                  <Button
                    disabled={isSavingLoading}
                    variant="contained"
                    color="primary"
                    onClick={() =>
                      checkUserPermission({
                        permission: PERMISSIONS.SERVICE_ORDER,
                        permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                        functionAccess: () => onSaveClick(),
                      })
                    }
                  >
                    Salvar
                  </Button>
                  <Button variant="outlined" onClick={onCancelEditClick}>
                    Cancelar
                  </Button>
                </>
              ) : (
                <>
                  {(!budget?.proposeOpened || budget?.tradesman) && (
                    <Link
                      component={RouterLink}
                      to={`/chamado/${serviceOrderId}/orcamento/${originalBudgetId}`}
                      className={styles.originalBudgetLink}
                    >
                      Orçamento original
                    </Link>
                  )}
                  {showSendBudgetByEmailButton && <SendBudgetByEmailButton />}
                  <SendBudgetByWhatsAppButton />
                  <Tooltip title="Baixar PDF">
                    <IconButton
                      onClick={handleValidBudget}
                      className={styles.downloadButton}
                      disabled={isLoading}
                    >
                      <Svg type={ICON.DOWNLOAD} />
                    </IconButton>
                  </Tooltip>
                  {modalOpen && (
                    <ModalConfirmInstallment
                      open={modalOpen}
                      onCancel={() => setModalOpen(false)}
                      budgetId={budgetId}
                    />
                  )}
                  {(isAdmin || isSAAS) && (
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() =>
                        checkUserPermission({
                          permission: PERMISSIONS.BUDGETS,
                          permissionAction: [PERMISSIONS_ACTIONS.ADD, PERMISSIONS_ACTIONS.EDIT],
                          functionAccess: handleEditClick,
                        })
                      }
                      endIcon={<Svg icon={EditIcon} className={styles.editIcon} />}
                    >
                      Editar
                    </Button>
                  )}
                </>
              )}
            </Grid>
          )}
        </Grid>
      </Grid>
      <ModalDialog modalDialog={modalDialog} setModalDialog={setModalDialog} />
    </>
  )
}

Actions.propTypes = {
  isCancelled: PropTypes.bool,
  serviceOrderId: PropTypes.number.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isSavingLoading: PropTypes.bool,
  onToggleEdit: PropTypes.func.isRequired,
  isOriginalBudget: PropTypes.bool.isRequired,
  onCancelEditClick: PropTypes.func.isRequired,
  onSaveClick: PropTypes.func.isRequired,
  // TODO: Remove eslint after BE sends this info
  originalBudgetId: PropTypes.number, // eslint-disable-line
  editableBudgetId: PropTypes.number, // eslint-disable-line
  onBackToServiceOrderClick: PropTypes.func.isRequired,
  onDownloadPdfClick: PropTypes.func,
  isDownloadPdfDialogOpen: PropTypes.func,
}

Actions.defaultProps = {
  isCancelled: false,
  originalBudgetId: undefined,
  editableBudgetId: undefined,
  onDownloadPdfClick: undefined,
  isDownloadPdfDialogOpen: undefined,
  isSavingLoading: false,
}

export default React.memo(Actions)
